import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('savenes_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/**
 * Maps URL path prefixes to the feature key they belong to. Used by the
 * sidebar + ProtectedRoute to figure out which menu items should be locked
 * on a trial plan.
 *
 * Any path NOT listed here is treated as "always available" (settings,
 * subscription, profile, auth flows, etc). The trial intentionally grants
 * a small core (dashboard + forms + settings) so users can fully evaluate
 * the form builder.
 */
const PATH_FEATURE_MAP = [
  { prefix: '/patients', feature: 'patients' },
  { prefix: '/appointments', feature: 'scheduling' },
  { prefix: '/scheduling', feature: 'scheduling' },
  { prefix: '/telehealth', feature: 'telehealth' },
  { prefix: '/claims', feature: 'claims' },
  { prefix: '/denials', feature: 'claims' },
  { prefix: '/mychart', feature: 'ehr' },
  { prefix: '/ehr', feature: 'ehr' },
  { prefix: '/fhir', feature: 'ehr' },
  { prefix: '/insights', feature: 'analytics' },
  { prefix: '/analytics', feature: 'analytics' },
  { prefix: '/reports', feature: 'analytics' },
  { prefix: '/cqm', feature: 'analytics' },
  { prefix: '/home-care', feature: 'home_care' },
  { prefix: '/admin/evv', feature: 'home_care' },
  { prefix: '/crm', feature: 'crm' },
  { prefix: '/admin/lms', feature: 'lms' },
  { prefix: '/learn', feature: 'lms' },
  { prefix: '/admin/knowledge', feature: 'lms' },
  { prefix: '/knowledge', feature: 'lms' },
  { prefix: '/admin/forum', feature: 'lms' },
  { prefix: '/forum', feature: 'lms' },
  { prefix: '/admin/csm', feature: 'csm' },
  { prefix: '/assets', feature: 'assets' },
  { prefix: '/widgets', feature: 'analytics' },
];

export function featureKeyForPath(path) {
  if (!path) return null;
  for (const entry of PATH_FEATURE_MAP) {
    if (path === entry.prefix || path.startsWith(entry.prefix + '/')) {
      return entry.feature;
    }
  }
  return null; // null means always available
}

const SubscriptionContext = createContext({
  sub: null, loading: true, hasFeature: () => true, isTrialOnly: false,
  isExpired: false, isTrialing: false, reload: () => {},
});

export function SubscriptionProvider({ children }) {
  const { user } = useAuth();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/subscription/me`, { headers: authHeader() });
      setSub(r.data);
    } catch (_e) {
      setSub(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Trigger when token changes (login/logout)
    const onStorage = (e) => { if (e.key === 'savenes_token') load(); };
    const onAuthChange = () => load();
    window.addEventListener('storage', onStorage);
    window.addEventListener('savenes:auth-changed', onAuthChange);
    load();
    // Keep the countdown fresh — drives the trial banner's day/hour copy.
    const tick = setInterval(load, 5 * 60 * 1000);
    return () => {
      clearInterval(tick);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('savenes:auth-changed', onAuthChange);
    };
  }, []);

  // A trial-only plan = subscription status='trialing' with feature_keys array
  // that doesn't include 'all'. Active paid plans get full access regardless of
  // feature_keys (they aren't restricted today).
  const status = sub?.status;
  const isTrialing = status === 'trialing';
  const isExpired = !!sub?.expired;
  // Treat "no subscription yet" as full access too (super admins, etc).
  const isTrialOnly = isTrialing && Array.isArray(sub?.feature_keys) && sub.feature_keys.length > 0;

  const hasFeature = (featureKey) => {
    // Super admin (Platform Team) is never gated — they need full access to
    // manage every customer org. Same for csm_agent (platform support).
    if (user && ['super_admin', 'csm_agent'].includes(user.role)) return true;
    if (isExpired) return false;        // Trial expired = locked out
    if (!isTrialOnly) return true;       // Paid / no-sub = full access
    if (!featureKey) return true;        // null = always available
    return sub.feature_keys.includes(featureKey);
  };

  const value = { sub, loading, hasFeature, isTrialOnly, isExpired, isTrialing, reload: load };
  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
}

export function useSubscription() {
  return useContext(SubscriptionContext);
}
